"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const core_1 = require("@capsizecss/core");
const plugin_1 = __importDefault(require("tailwindcss/plugin"));
const utils_js_1 = require("./utils.js");
exports.default = plugin_1.default.withOptions(({ rootSize = 16, className = 'capsize', mode = 'modern' } = {}) => {
    if (mode === 'classic') {
        return function ({ addUtilities, theme }) {
            let fontMetrics = theme('fontMetrics', {});
            let lineHeight = theme('lineHeight', {});
            let fontSize = theme('fontSize', {});
            let utilities = {};
            for (let fontFamily of Object.keys(fontMetrics)) {
                let fontConfig = fontMetrics[fontFamily];
                for (let sizeName of Object.keys(fontSize)) {
                    for (let leading of Object.keys(lineHeight)) {
                        let fs = (0, utils_js_1.normalizeValue)(fontSize[sizeName], rootSize);
                        let lh = (0, utils_js_1.normalizeValue)(lineHeight[leading], rootSize, fs);
                        let { '::after': after, '::before': before } = (0, core_1.createStyleObject)({
                            fontMetrics: fontConfig,
                            fontSize: fs,
                            leading: lh,
                        });
                        utilities[(0, utils_js_1.makeCssSelectors)(fontFamily, sizeName, leading, className)] =
                            {
                                '&::before': before,
                                '&::after': after,
                            };
                    }
                }
            }
            addUtilities(utilities, {});
        };
    }
    return function ({ addUtilities, matchUtilities, e, theme }) {
        let fontMetrics = theme('fontMetrics', {});
        let fontFamily = theme('fontFamily', {}) ?? {};
        // Font-family
        matchUtilities({
            font(value) {
                function fallback(family) {
                    return {
                        'font-family': family,
                    };
                }
                let family = (0, utils_js_1.normalizeThemeValue)('fontFamily', value);
                let familyKey = Object.keys(fontFamily).find((key) => fontFamily[key] === value);
                if (familyKey === undefined)
                    return fallback(family);
                let metrics = fontMetrics[familyKey];
                if (metrics === undefined)
                    return fallback(family);
                let { ascent, descent, lineGap, unitsPerEm, capHeight } = metrics;
                let ascentScale = ascent / unitsPerEm;
                let descentScale = Math.abs(descent) / unitsPerEm;
                let capHeightScale = capHeight / unitsPerEm;
                let lineGapScale = lineGap / unitsPerEm;
                let lineHeightScale = (ascent + lineGap + Math.abs(descent)) / unitsPerEm;
                return {
                    '--ascent-scale': (0, utils_js_1.round)(ascentScale),
                    '--descent-scale': (0, utils_js_1.round)(descentScale),
                    '--cap-height-scale': (0, utils_js_1.round)(capHeightScale),
                    '--line-gap-scale': (0, utils_js_1.round)(lineGapScale),
                    '--line-height-scale': (0, utils_js_1.round)(lineHeightScale),
                    'font-family': family,
                };
            },
        }, {
            values: theme('fontFamily'),
            type: ['lookup', 'generic-name', 'family-name'],
        });
        // Font-size
        matchUtilities({
            // @ts-expect-error -- Extra custom properties mismatches base type.
            text(value) {
                let [fontSize, options] = Array.isArray(value) ? value : [value];
                let fontSizeActual = (0, utils_js_1.normalizeValue)(fontSize, rootSize);
                let { lineHeight, letterSpacing, fontWeight } = ((0, utils_js_1.isPlainObject)(options) ? options : { lineHeight: options });
                return {
                    '--font-size-px': String(fontSizeActual),
                    'font-size': fontSize,
                    ...(0, utils_js_1.lineHeightProperties)(lineHeight, rootSize),
                    ...(letterSpacing === undefined
                        ? {}
                        : { 'letter-spacing': letterSpacing }),
                    ...(fontWeight === undefined ? {} : { 'font-weight': fontWeight }),
                };
            },
        }, {
            values: theme('fontSize'),
            type: ['absolute-size', 'relative-size', 'length', 'percentage'],
        });
        // Line-height
        matchUtilities({
            // @ts-expect-error -- Extra custom properties mismatches base type.
            leading(value) {
                let lineHeight = (0, utils_js_1.normalizeThemeValue)('lineHeight', value);
                return (0, utils_js_1.lineHeightProperties)(lineHeight, rootSize);
            },
        }, {
            values: theme('lineHeight'),
        });
        // Leading-trim
        addUtilities({
            [`.${e(className)}`]: {
                '&::before': {
                    display: 'table',
                    content: '""',
                    'margin-bottom': 'calc(((var(--ascent-scale) - var(--cap-height-scale) + var(--line-gap-scale) / 2) - var(--line-height-offset)) * -1em)',
                },
                '&::after': {
                    display: 'table',
                    content: '""',
                    'margin-top': 'calc(((var(--descent-scale) + var(--line-gap-scale) / 2) - var(--line-height-offset)) * -1em)',
                },
            },
        }, {});
    };
}, ({ mode = 'modern' } = {}) => {
    if (mode === 'classic')
        return {};
    return {
        corePlugins: {
            fontFamily: false,
            fontSize: false,
            lineHeight: false,
        },
    };
});
